<template>
  <div class="flex items-center">
    <div class="flex items-center">
      <svg
        v-for="(star, index) in stars"
        :key="index"
        :class="[
          'transition-colors duration-200',
          sizeClasses,
          star.filled ? fillColor : emptyColor
        ]"
        fill="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs v-if="star.partial">
          <linearGradient :id="`gradient-${index}`" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" :stop-color="fillColorValue" />
            <stop :offset="`${star.fillPercentage}%`" :stop-color="fillColorValue" />
            <stop :offset="`${star.fillPercentage}%`" :stop-color="emptyColorValue" />
            <stop offset="100%" :stop-color="emptyColorValue" />
          </linearGradient>
        </defs>
        <path
          :fill="star.partial ? `url(#gradient-${index})` : 'currentColor'"
          fill-rule="evenodd"
          d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.007z"
          clip-rule="evenodd"
        />
      </svg>
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  value: number;
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  readonly?: boolean;
  fillColor?: string;
  emptyColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  max: 5,
  size: 'md',
  readonly: true,
  fillColor: 'text-amber-400',
  emptyColor: 'text-gray-300'
});

// Size classes for different star sizes
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };
  return sizes[props.size];
});

// Get the actual color values for gradients
const fillColorValue = computed(() => {
  const colorMap: Record<string, string> = {
    'text-amber-400': '#fbbf24',
    'text-yellow-400': '#facc15',
    'text-orange-400': '#fb923c',
    'text-red-400': '#f87171',
    'text-blue-400': '#60a5fa',
    'text-green-400': '#4ade80',
    'text-purple-400': '#c084fc',
    'text-pink-400': '#f472b6'
  };
  return colorMap[props.fillColor] || '#fbbf24';
});

const emptyColorValue = computed(() => {
  const colorMap: Record<string, string> = {
    'text-gray-300': '#d1d5db',
    'text-gray-200': '#e5e7eb',
    'text-gray-400': '#9ca3af'
  };
  return colorMap[props.emptyColor] || '#d1d5db';
});

// Calculate star display data
const stars = computed(() => {
  const result = [];
  const fullStars = Math.floor(props.value);
  const hasPartialStar = props.value % 1 !== 0;
  const partialValue = props.value % 1;

  // Full stars
  for (let i = 0; i < fullStars; i++) {
    result.push({
      filled: true,
      partial: false,
      fillPercentage: 100
    });
  }

  // Partial star
  if (hasPartialStar && result.length < props.max) {
    result.push({
      filled: false,
      partial: true,
      fillPercentage: partialValue * 100
    });
  }

  // Empty stars
  while (result.length < props.max) {
    result.push({
      filled: false,
      partial: false,
      fillPercentage: 0
    });
  }

  return result;
});
</script>